import {
  Select,
  SelectItem,
  SelectPopover,
  SelectProvider,
  useSelectStore,
  useStoreState,
} from '@ariakit/react';
import { FloatingNode, useFloatingNodeId } from '@floating-ui/react';
import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import { useFormMode } from 'src/components/form/form-mode-context';
import type { FormOption } from 'src/components/form/form-option';
import { InnerAddon } from 'src/components/form/inner-addon';
import { FieldLabel } from 'src/components/form/label';
import { RemoveButton } from 'src/components/form/select/components/remove-button';
import { SelectToggleIcon } from 'src/components/form/select/components/select-toggle';
import { Separator } from 'src/components/form/select/components/separator';
import type { SortByOptions } from 'src/components/form/select/utils';
import {
  applySortBy,
  selectOptionBaseClasses,
} from 'src/components/form/select/utils';
import { placeholderLikeClasses } from 'src/components/form/styles';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { flyoutClasses } from 'src/styles';
import { cn } from 'src/utils/cn';

type SingleSelectProps<Value extends string | null = string> = {
  stacked?: boolean;
  inline?: boolean;
  initialOpen?: boolean;
  /**
   * If you have an option with the value `null` this indicates that the field
   * can be emptied (via our remove button) even though it is not directly shown in the list of options.
   * Furthermore the label of this option will be used as placeholder.
   */
  options: FormOption<Value>[];
  onChange?: (value: Value) => void;
  pending?: boolean;
  /**
   * The `placeholder` should guide the user to provide valid input. Refer to the [placeholder pattern documentation](./?path=/docs/patterns-content-placeholders--docs) for more information.
   */
  placeholder?: string;
  sortBy?: SortByOptions;
  renderOptions?: (props: { children: ReactNode }) => ReactNode;
} & FormFieldProps;

export function SingleSelect<Value extends string | null = string>(
  props: SingleSelectProps<Value>
) {
  const { isStacked, minTablet } = useDefaultStacked();
  const {
    name,
    label,
    onChange,
    pending,
    placeholder,
    stacked = isStacked,
    inline,
    options,
    hideLabel = false,
    disabled,
    disabledMessage,
    hint,
    info,
    sortBy = 'label',
    markAsRequired,
    initialOpen,
    renderOptions,
  } = props;

  const [field, showError, error, helper] = useCustomField<Value>(name, label);

  const formGroup = useFieldGroup();
  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  const id = useId();
  const fieldId = formGroup?.id || id;
  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;

  const fieldItem = (
    <FieldItem>
      <Tooltip
        content={disabled && disabledMessage ? disabledMessage : undefined}
      >
        {(targetProps) => (
          <SimpleSingleSelect
            id={fieldId}
            options={options}
            showError={showError}
            placeholder={placeholder}
            disabled={disabled}
            pending={pending}
            {...field}
            onChange={(value) => {
              helper.setValue(value);
              onChange?.(value);
            }}
            value={field.value}
            sortBy={sortBy}
            initialOpen={initialOpen}
            renderOptions={renderOptions}
            {...targetProps}
          />
        )}
      </Tooltip>

      {hint && <FormHint isDisabled={props.disabled}>{hint}</FormHint>}

      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
        />
      )}
    </FieldItem>
  );

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked} inline={inline}>
      <FieldLabel
        name={name}
        displayLabel={displayLabel}
        fieldId={fieldId}
        info={info}
        showError={showError}
      />

      {fieldItem}
    </FieldLayout>
  ) : (
    fieldItem
  );
}

type SimpleSingleSelectProps<Value extends string | null = string> = {
  id?: string;
  name: string;
  showError?: boolean;
  placeholder?: string;
  options: FormOption<Value>[];
  disabled?: boolean;
  pending?: boolean;
  onChange: (value: Value) => void;
  value: Value;
  sortBy?: SortByOptions;
  initialOpen?: boolean;
  renderOptions?: (props: { children: ReactNode }) => ReactNode;
  /**
   * Be careful when you switch the mode. In regular forms you should always use `default` mode.
   * You likely only want to use `ghost` mode if it is part of a bigger composite component.
   */
  mode?: 'default' | 'ghost';
};

export function SimpleSingleSelect<Value extends string | null = string>({
  id,
  name,
  showError,
  disabled,
  pending,
  value,
  onChange,
  options: unsortedOptions,
  placeholder = 'Please Select',
  sortBy = 'label',
  initialOpen,
  renderOptions = ({ children }) => children,
  mode = 'default',
}: SimpleSingleSelectProps<Value>) {
  const options = applySortBy(sortBy, unsortedOptions);
  const hasSingleOption = options.length === 1;
  const singleOptionValue = hasSingleOption ? options[0].value : false;

  // for backwards compatibility, we need to support both empty strings and null as empty values
  const emptyOptionAvailable =
    options[0]?.value === '' || options[0]?.value === null; // when ordered empty value would be the first one
  // it can also happen that a select value is required and can only be empty initially,
  // but not picked from the list of options.
  const initialEmptyValueRef = useRef<Value | null>(
    value === '' || value === null ? value : null // fallback to null
  );
  const emptyValue = emptyOptionAvailable
    ? options[0]?.value
    : initialEmptyValueRef.current;

  const isDisabled =
    options.length === 0 || hasSingleOption || disabled || pending;

  // prefer label of empty value as placeholder
  const chosenPlaceholder = emptyOptionAvailable
    ? options[0]?.label
    : placeholder;

  const formMode = useFormMode();
  const isForm = formMode === 'regular';

  const focusRef = useFocusManager<HTMLButtonElement>({ type: 'click' });

  // in case the value is required, but the initial value is empty it can also happen
  // that there is no selected item at all.
  // in those cases we show the placeholder.
  const selectedItem = options.find((item) => item.value === value);

  // auto-select single option
  useEffect(() => {
    if (singleOptionValue === false) return;
    if (value !== singleOptionValue) {
      onChange(singleOptionValue);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [value, singleOptionValue]);

  const showRemoveButton =
    emptyOptionAvailable &&
    options.length > 0 &&
    selectedItem &&
    selectedItem.value !== emptyValue &&
    isForm;

  const store = useSelectStore({
    value: value ?? '',
    setValue(value) {
      onChange((value === emptyValue ? emptyValue : value) as Value);
    },
    defaultOpen: initialOpen,
  });
  const isOpen = useStoreState(store, 'open');
  const activeId = useStoreState(store, 'activeId');

  const [popoverFocused, setPopoverFocused] = useState(false);

  const nodeId = useFloatingNodeId();

  return (
    <div>
      {/*
        for backwards compatibility: by using <input type="hidden"/> we can access the value easily in tests.
        this is needed since we switched from using a native <select /> to a custom select component.
      */}
      <input type="hidden" data-testid={name} value={value ?? ''} />

      <SelectProvider store={store}>
        <InnerAddon
          className={cn(
            mode === 'ghost' && `legacy:[&>div]:pr-0 legacy:[&>div]:pl-[0.5rem]`
          )}
          right={
            <div
              className={cn(
                `
                  grid grid-flow-col items-center gap-xs
                  legacy:gap-[0.5rem]
                `,
                mode === 'ghost' && `not-legacy:hidden!`
              )}
            >
              {showRemoveButton && (
                <div
                  className={`
                    grid grid-flow-col items-center gap-xs
                    legacy:gap-[0.5rem]
                  `}
                >
                  <RemoveButton
                    error={showError}
                    disabled={isDisabled}
                    data-testid={`${name}-remove-selection`}
                    onClick={() => {
                      onChange(emptyValue as Value);
                    }}
                    className="[pointer-events:initial]"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </RemoveButton>
                  <Separator error={showError} disabled={isDisabled} />
                </div>
              )}

              <SelectToggleIcon
                showError={showError}
                disabled={isDisabled}
                isOpen={isOpen}
              />
            </div>
          }
        >
          {({ leftBounds, rightBounds }) => (
            <Select
              id={id}
              ref={focusRef}
              disabled={isDisabled}
              data-name={name} // for focus manager
              data-testid={`${name}-togglebutton`}
              data-test-error={showError}
              style={
                {
                  '--field-padding-left': leftBounds
                    ? `${leftBounds.width}px`
                    : undefined,
                  '--field-padding-right': rightBounds
                    ? `${rightBounds.width}px`
                    : undefined,
                } as CSSProperties
              }
              className={cn(
                getSelectToggleBaseClasses({
                  isPlaceholder: selectedItem
                    ? selectedItem.value === emptyValue
                    : true,
                  showError,
                }),
                leftBounds &&
                  `
                    pl-(--field-padding-left)
                    legacy:pl-(--field-padding-left)
                  `,
                rightBounds &&
                  `
                    pr-(--field-padding-right)
                    legacy:pr-(--field-padding-right)
                  `,
                isOpen && popoverFocused && 'not-legacy:focus-style',
                mode === 'ghost' &&
                  `
                    border-none p-0! text-foreground-secondary-default
                    not-legacy:text-foreground-secondary-default
                    legacy:pr-[1.5rem]!
                  `
              )}
            >
              <span className="truncate">
                {pending
                  ? 'Loading...'
                  : selectedItem
                    ? selectedItem.label
                    : chosenPlaceholder}
              </span>
            </Select>
          )}
        </InnerAddon>

        <FloatingNode id={nodeId}>
          {createPortal(
            <SelectPopover
              data-testid={`${name}-options`}
              unmountOnHide
              className={selectPopoverClasses}
              onFocus={() => setPopoverFocused(true)}
              onBlur={(event) => {
                // check if focus stays inside the popover on an option
                if (
                  event.relatedTarget?.contains(event.target) &&
                  event.target.getAttribute('role') === 'option'
                )
                  return;

                setPopoverFocused(false);
              }}
            >
              {renderOptions({
                children: options.map((option) => (
                  <ItemWithTooltips
                    key={option.value}
                    activeId={activeId}
                    option={option}
                    emptyValue={emptyValue}
                    value={value}
                    id={id}
                    showError={showError}
                    name={name}
                  />
                )),
              })}
            </SelectPopover>,
            document.body
          )}
        </FloatingNode>
      </SelectProvider>
    </div>
  );
}

// mirror SimpleInput style
export const getSelectToggleBaseClasses = ({
  showError,
  isPlaceholder,
}: {
  showError?: boolean;
  isPlaceholder?: boolean;
}) =>
  cn(
    '[pointer-events:initial]!', // https://github.com/ariakit/ariakit/issues/845#issuecomment-785493623
    `
      m-0 inline-grid w-full cursor-default appearance-none items-center
      justify-between overflow-hidden border border-border-surface-default
      bg-white p-md text-left text-foreground-surface-default
      not-legacy:cursor-pointer not-legacy:rounded-xs
      legacy:border-legacy-brand legacy:px-[2rem] legacy:pt-[0.6rem]
      legacy:pb-[0.7rem] legacy:text-legacy-brand
    `,
    `data-focus-visible:focus-style`,
    `
      aria-[disabled]:cursor-not-allowed aria-[disabled]:border-legacy-inactive
      aria-[disabled]:bg-legacy-inactive-lighter
      aria-[disabled]:text-legacy-inactive
    `,
    showError &&
      `
        border-border-support-error-subdued bg-background-support-error-minimal
        text-foreground-support-error
        legacy:border-legacy-error legacy:bg-legacy-error-lighter
        legacy:text-legacy-error
      `,
    isPlaceholder && placeholderLikeClasses,
    isPlaceholder &&
      `
        aria-[disabled]:text-foreground-surface-disabled
        legacy:aria-[disabled]:text-legacy-inactive
      `,
    isPlaceholder &&
      showError &&
      `
        text-foreground-support-error
        legacy:text-legacy-error
      `
  );

export const selectPopoverClasses = cn(
  flyoutClasses,
  `
    inset-x-0 max-h-[23.5rem] w-(--popover-anchor-width)
    max-w-(--popover-available-width) min-w-[17.9rem] overflow-auto
    not-legacy:flex not-legacy:flex-col not-legacy:gap-xs not-legacy:px-xs
    not-legacy:py-md
  `,
  // by default ariakit keeps the popover focused and uses the
  // activedescendant pattern to manage a virtualfocus for the active item.
  // we handle the focus style for them manually.
  'focus-visible:outline-none'
);

function ItemWithTooltips<Value extends string | null = string>({
  id,
  activeId,
  option,
  value,
  emptyValue,
  name,
}: {
  id?: string;
  activeId: string | null | undefined;
  option: FormOption<Value>;
  value: string | null | undefined;
  emptyValue: Value | null | undefined;
  showError?: boolean;
  name: string;
}) {
  const infoRef = useRef<HTMLElement>(null);
  const itemTestId = `${name}-option-${option.value}`;
  const itemId = id ? `${id}-${itemTestId}` : itemTestId;

  const active = activeId === itemId;

  if (option.value === emptyValue) return null;

  return (
    <Tooltip
      key={option.value}
      content={option.disabled && option.disabledMessage}
      noTabIndex
      controlOpen={active}
    >
      {(disabledMessageTargetProps) => (
        <Tooltip
          content={option.info}
          positionRef={infoRef}
          noTabIndex
          controlOpen={active}
        >
          {(infoTargetProps) => (
            <SelectItem
              {...infoTargetProps}
              {...disabledMessageTargetProps}
              disabled={option.disabled}
              value={option.value ?? ''}
              accessibleWhenDisabled // make it possible to discover disabled items via keyboard
              id={itemId}
              data-testid={itemTestId}
              className={cn(
                selectOptionBaseClasses,
                `
                  aria-selected:bg-background-surface-selected
                  aria-selected:text-foreground-surface-selected
                  [[data-active-item]:not([aria-selected='true'],[aria-disabled])]:bg-background-surface-hover
                  [[data-active-item]:not([aria-selected='true'],[aria-disabled])]:text-foreground-surface-hover
                  not-legacy:[&:not([aria-selected='true'],[aria-disabled])]:hover:bg-background-surface-hover
                  not-legacy:[&:not([aria-selected='true'],[aria-disabled])]:hover:text-foreground-surface-hover
                `,
                `
                  legacy:aria-selected:bg-legacy-brand-light-2
                  legacy:aria-selected:text-white
                  legacy:[[data-active-item]:not([aria-selected='true'],[aria-disabled])]:bg-legacy-background
                  legacy:[[data-active-item]:not([aria-selected='true'],[aria-disabled])]:text-legacy-brand
                `
              )}
            >
              <div
                className={`
                  grid grid-flow-col items-baseline justify-between gap-xl
                  legacy:gap-[2rem]
                `}
              >
                <span>
                  {option.label}
                  {option.info && (
                    <>
                      {' '}
                      <FontAwesomeIcon
                        forwardedRef={infoRef}
                        icon={faInfoCircle}
                        className={cn(
                          option.value === value
                            ? 'text-white'
                            : 'text-legacy-brand'
                        )}
                      />
                    </>
                  )}
                </span>

                {option.value && option.value === value && (
                  <FontAwesomeIcon
                    data-testid={`${itemTestId}-isSelected`}
                    icon={faCheck}
                  />
                )}
              </div>

              {option.hint && (
                <FormHint
                  isDisabled={option.value !== value && option.disabled}
                  isSelected={option.value === value}
                >
                  {option.hint}
                </FormHint>
              )}
            </SelectItem>
          )}
        </Tooltip>
      )}
    </Tooltip>
  );
}
